import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Format } from '@gg/shared';
import { Badge, Button, Notice, PageHeader, SelectableCard, StickyFooter } from '@/components/ui';
import { funnel } from '@/lib/analytics';
import { loadDraft, saveDraft } from '@/lib/checkoutDraft';
import { formatDate, formatDay, formatLastDay, formatNumber, formatTime } from '@/lib/dates';
import {
  eventEyebrow,
  isDiscounted,
  isPurchasable,
  isTeamFormat,
  localName,
  teamSizeLabel,
  toggleSelection,
} from '@/lib/formats';
import { formatPrice } from '@/lib/i18n';
import { useAvailableFormats, useEvent } from '@/lib/queries';

/** Screen 01: format selection (P-01). Multi-select, sticky total, one CTA. */
export function Formats() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { data: event, isError } = useEvent();
  // "Ajouter une course" from the profile lands with ?add=1 (P-13): a fresh selection among
  // the formats not bought yet. The mode is kept in the draft until the order is paid.
  const [upsell] = useState(() => {
    if (params.get('add') === '1') {
      saveDraft({ upsell: true, formatIds: [] });
      return true;
    }
    return loadDraft().upsell === true;
  });
  const [selected, setSelected] = useState<string[]>(() => loadDraft().formatIds);
  // Back on the format selection from an invitation link (P-21): the visitor chooses for
  // themselves again, the team context no longer applies to what they pick here.
  useEffect(() => {
    if (loadDraft().joinCode) saveDraft({ joinCode: undefined, team: undefined });
  }, []);
  const available = useAvailableFormats(upsell);
  // Session gone: back to the normal checkout with the email step.
  const upsellActive = upsell && !available.isError;
  useEffect(() => {
    if (upsell && available.isError) saveDraft({ upsell: false });
  }, [upsell, available.isError]);

  useEffect(() => {
    funnel.viewFormats();
  }, []);

  // Invitation links land here with ?ref=: keep the code for the order (PRD §4.2).
  const ref = params.get('ref');
  useEffect(() => {
    if (ref) saveDraft({ referralCode: ref });
  }, [ref]);
  // Drop ?add=1 so a reload keeps the selection instead of starting over.
  useEffect(() => {
    if (params.has('add')) navigate('/formats', { replace: true });
  }, [params, navigate]);

  const formats = upsellActive
    ? (event?.formats ?? []).filter((f) => available.data?.formatIds.includes(f.id))
    : (event?.formats ?? []);
  const loading = !event || (upsellActive && !available.data);
  // A format that sold out since the last visit drops out of the restored selection.
  const chosen = formats.filter((f) => selected.includes(f.id) && isPurchasable(f));
  const total = chosen.reduce((s, f) => s + (f.currentTier?.priceCents ?? 0), 0);

  // Intro: earliest early-bird end among the discounted formats.
  const earlyBirdEnd = formats
    .filter(isDiscounted)
    .map((f) => f.currentTier?.endsAt)
    .filter((d): d is string => Boolean(d))
    .sort()[0];

  const toggle = (f: Format, on: boolean) => {
    const next = toggleSelection(selected, formats, f, on);
    setSelected(next);
    saveDraft({ formatIds: next });
    if (on) funnel.selectFormat(f.id, f.name, f.currentTier?.priceCents ?? 0);
  };

  const onContinue = () => {
    saveDraft({ formatIds: chosen.map((f) => f.id) });
    navigate('/register');
  };

  return (
    <div className="flex flex-1 flex-col pt-14">
      <PageHeader
        eyebrow={event && eventEyebrow(event)}
        title={t('formats.title')}
        intro={
          <>
            {t('formats.intro')}
            {earlyBirdEnd &&
              ` ${t('formats.earlyBirdUntil', { date: formatLastDay(earlyBirdEnd) })}`}
          </>
        }
      />

      <div className="flex flex-col gap-3 pt-5 pb-2">
        {isError && <p className="text-danger">{t('error.generic')}</p>}
        {!loading && formats.length === 0 && (
          <p className="text-ink-muted">
            {upsellActive ? t('formats.allBought') : t('formats.empty')}
          </p>
        )}
        {formats.map((f) => (
          <FormatCard
            key={f.id}
            format={f}
            selected={chosen.some((c) => c.id === f.id)}
            onToggle={(on) => toggle(f, on)}
          />
        ))}
        {chosen.some(isTeamFormat) && <Notice>{t('formats.teamAlone')}</Notice>}
      </div>

      <StickyFooter
        totalLabel={t('formats.selected', { count: chosen.length })}
        total={formatPrice(total)}
      >
        <Button disabled={chosen.length === 0} onClick={onContinue}>
          {t('formats.continue')}
        </Button>
      </StickyFooter>
    </div>
  );
}

function FormatCard({
  format: f,
  selected,
  onToggle,
}: {
  format: Format;
  selected: boolean;
  onToggle: (on: boolean) => void;
}) {
  const { t } = useTranslation();
  const details = [
    f.startsAt && formatDay(f.startsAt, 'long'),
    f.elevationM !== null && t('formats.elevation', { value: formatNumber(f.elevationM) }),
    f.startsAt && t('formats.start', { time: formatTime(f.startsAt) }),
  ]
    .filter(Boolean)
    .join(' · ');
  // Team format (P-20): "Équipe de 2 · le capitaine paie, les coéquipier·ères rejoignent par lien".
  const team = isTeamFormat(f) ? `${teamSizeLabel(t, f)} · ${t('formats.teamHint')}` : null;
  const discounted = isDiscounted(f);

  return (
    <SelectableCard
      title={localName(f.name, f.nameEn)}
      price={f.currentTier && formatPrice(f.currentTier.priceCents)}
      details={details || undefined}
      footer={
        (team || discounted || f.requiresMedicalDocument) && (
          <span className="flex flex-col gap-1">
            {team && <span className="text-sm text-ink-muted">{team}</span>}
            {discounted && (
              <span className="inline-flex items-center gap-1.5">
                <Badge tone="accent">{t('formats.earlyBird')}</Badge>
                <s className="text-helper text-ink-subtle">
                  {formatPrice(f.standardPriceCents ?? 0)}
                </s>
              </span>
            )}
            {f.requiresMedicalDocument && (
              <span className="text-helper text-ink-muted">{t('formats.medicalDocument')}</span>
            )}
          </span>
        )
      }
      selected={selected}
      checked={selected}
      onChange={(e) => onToggle(e.target.checked)}
      soldOut={!isPurchasable(f)}
      soldOutLabel={
        f.currentTier
          ? t('formats.soldOut')
          : f.opensAt
            ? t('formats.opensOn', { date: formatDate(f.opensAt) })
            : t('formats.closed')
      }
    />
  );
}
